import { Editor, EditorState, RichUtils } from "draft-js";
import { LinkEditComponent, LinkComponent } from "./plugins/link";
import { Dispatch, SetStateAction, useEffect, useRef, useState } from "react";
import { BookComponent } from "./plugins/book";
import {
  constants,
  createEmptyState,
  getEntityIfSelected,
  onButtonClick,
  mediaBlockRenderer,
} from "./editor-utils";
import { EventComponent } from "./plugins/event";
import { ImageComponent } from "./plugins/image";
import { VideoComponent } from "./plugins/video";

interface Props {
  state: EditorState;
  setState: Dispatch<SetStateAction<EditorState>>;
  disableH2?: boolean;
  disableH3?: boolean;
  disableBooks?: boolean;
  disableEvents?: boolean;
  disableImages?: boolean;
  disableVideo?: boolean;
  readOnly?: boolean;
  disableToolbar?: boolean;
}

export const TextEditor = ({
  state,
  setState,
  disableBooks,
  disableEvents,
  disableH2,
  disableH3,
  disableImages,
  disableVideo,
  disableToolbar,
  readOnly = false,
}: Props) => {
  const [hasMounted, setMounted] = useState<boolean>(false);
  useEffect(() => {
    if (!state) {
      setState(createEmptyState());
    }
    setMounted(true);
  }, []);

  const editorRef = useRef(null);
  function focusEditor() {
    try {
      editorRef.current.focus();
    } catch (e) {
      return null;
    }
  }

  const handleKeyCommand = (command) => {
    const newState = RichUtils.handleKeyCommand(state, command);
    if (newState) {
      setState(newState);
      return "handled";
    }
    return "not-handled";
  };

  if (hasMounted && !!state) {
    const isCursorOnLink = getEntityIfSelected(
      constants.EntityTypes.LINK,
      state
    );

    return (
      <article className='bg-white border border-gray-200 rounded-md'>
        {!disableToolbar && (
          <section className='border-b border-gray-400 flex flex-row flex-wrap justify-center sticky top-0 bg-white z-10 rounded-t-md'>
            <button
              type='button'
              title='bold'
              aria-label='bold'
              onClick={() =>
                onButtonClick(constants.InlineTypes.BOLD, state, setState)
              }
              className='bg-white hover:bg-gray-400 text-gray-800 py-1 px-3 rounded font-bold'>
              B
            </button>
            <button
              type='button'
              title='italic'
              aria-label='italic'
              onClick={() =>
                onButtonClick(constants.InlineTypes.ITALIC, state, setState)
              }
              className='bg-white hover:bg-gray-400 text-gray-800 font-semibold py-1 px-3 rounded italic'>
              I
            </button>
            <button
              type='button'
              title='underline'
              aria-label='underline'
              onClick={() =>
                onButtonClick(constants.InlineTypes.UNDERLINE, state, setState)
              }
              className='bg-white hover:bg-gray-400 text-gray-800 font-semibold py-1 px-3 rounded underline'>
              U
            </button>
            <button
              type='button'
              title='strikethrough'
              aria-label='strikethrough'
              onClick={() =>
                onButtonClick(
                  constants.InlineTypes.STRIKETHROUGH,
                  state,
                  setState
                )
              }
              className='bg-white hover:bg-gray-400 text-gray-800 font-semibold py-1 px-3 rounded line-through'>
              S
            </button>
            <div className='border-l border-gray-400 inline'></div>
            {!disableH2 && (
              <button
                type='button'
                title='header two'
                aria-label='header two'
                onClick={() =>
                  onButtonClick(constants.BlockTypes.H2, state, setState)
                }
                className='bg-white hover:bg-gray-400 text-gray-800 font-semibold py-1 px-3 rounded'>
                H2
              </button>
            )}
            {!disableH3 && (
              <button
                type='button'
                title='header three'
                aria-label='header three'
                onClick={() =>
                  onButtonClick(constants.BlockTypes.H3, state, setState)
                }
                className='bg-white hover:bg-gray-400 text-gray-800 font-semibold py-1 px-3 rounded'>
                H3
              </button>
            )}
            <div className='border-l border-gray-400 inline'></div>
            <button
              type='button'
              title='ordered list'
              aria-label='ordered list'
              onClick={() =>
                onButtonClick(constants.BlockTypes.OL, state, setState)
              }
              className='bg-white hover:bg-gray-400 text-gray-800 font-semibold px-2 rounded'>
              <svg
                className='w-8 h-8'
                xmlns='http://www.w3.org/2000/svg'
                viewBox='0 0 20 20'
                fill='currentColor'>
                <g fill='none' fillRule='evenodd'>
                  <g
                    stroke='currentColor'
                    strokeLinecap='round'
                    strokeLinejoin='round'>
                    <path d='M15.5 10.5h-7M15.5 14.5h-7M15.5 6.5h-7' />
                  </g>
                  <path
                    d='M5.88 8V5.828h-.037l-.68.459V5.67l.717-.488h.717V8zM4.9 10.068c0-.572.45-.963 1.109-.963.652 0 1.04.354 1.04.836 0 .334-.148.555-.597.961l-.555.502v.037h1.186V12H4.94v-.479l1.008-.912c.348-.318.406-.44.406-.605 0-.195-.136-.358-.382-.358-.262 0-.416.178-.416.422zm.712 4.73v-.484h.362c.238 0 .392-.138.392-.341 0-.192-.146-.332-.388-.332-.254 0-.409.134-.42.363h-.653c.01-.541.438-.899 1.108-.899.66 0 1.021.346 1.02.766 0 .34-.22.565-.528.637v.037c.406.057.64.309.64.68 0 .504-.48.851-1.158.851-.67 0-1.125-.361-1.15-.916h.684c.01.217.185.352.457.352.261 0 .439-.143.439-.356 0-.222-.168-.357-.443-.357z'
                    fill='#2a2e3b'
                  />
                </g>
              </svg>
            </button>
            <button
              type='button'
              title='unordered list'
              aria-label='unordered list'
              onClick={() =>
                onButtonClick(constants.BlockTypes.UL, state, setState)
              }
              className='bg-white hover:bg-gray-400 text-gray-800 font-semibold px-2 rounded  '>
              <svg
                className='w-8 h-8'
                xmlns='http://www.w3.org/2000/svg'
                viewBox='0 0 20 20'
                fill='currentColor'>
                <g fill='none' fillRule='evenodd'>
                  <g
                    stroke='currentColor'
                    strokeLinecap='round'
                    strokeLinejoin='round'>
                    <path d='M15.5 10.5h-7M15.5 14.5h-7M15.5 6.5h-7' />
                  </g>
                  <path
                    d='M5.499 7.5c.5 0 1-.5 1-1s-.5-1-1-1-.999.5-.999 1 .499 1 .999 1zm0 4c.5 0 1-.5 1-1s-.5-1-1-1-.999.5-.999 1 .499 1 .999 1zm0 4c.5 0 1-.5 1-1s-.5-1-1-1-.999.5-.999 1 .499 1 .999 1z'
                    fill='#2a2e3b'
                  />
                </g>
              </svg>
            </button>
            <button
              type='button'
              title='blockquote'
              aria-label='blockquote'
              onClick={() =>
                onButtonClick(constants.BlockTypes.BLOCKQUOTE, state, setState)
              }
              className='bg-white hover:bg-gray-400 text-gray-800 font-semibold px-2 rounded  '>
              <svg
                className='w-5 h-5'
                xmlns='http://www.w3.org/2000/svg'
                fill='none'
                viewBox='0 0 24 24'
                stroke='currentColor'>
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M9 5l7 7-7 7'
                />
              </svg>
            </button>
            <div className='border-l border-gray-400 inline'></div>
            <LinkComponent editorState={state} setEditorState={setState} />
            {!disableBooks && (
              <BookComponent editorState={state} setEditorState={setState} />
            )}
            {!disableEvents && (
              <EventComponent editorState={state} setEditorState={setState} />
            )}
            {!disableImages && (
              <ImageComponent editorState={state} setEditorState={setState} />
            )}
            {!disableVideo && (
              <VideoComponent editorState={state} setEditorState={setState} />
            )}
          </section>
        )}
        <section className='px-3 py-2' onClick={focusEditor}>
          <div
            onClick={focusEditor}
            className='min-h-full cursor-text relative prose mx-auto'>
            <Editor
              blockRendererFn={mediaBlockRenderer}
              readOnly={readOnly}
              ref={editorRef}
              editorState={state}
              handleKeyCommand={handleKeyCommand}
              onChange={setState}
            />
            {isCursorOnLink && (
              <LinkEditComponent
                blockKey={isCursorOnLink.blockKey}
                entityKey={isCursorOnLink.entityKey}
                url={isCursorOnLink.data.url}
                editorState={state}
                updateEditorState={setState}
              />
            )}
          </div>
        </section>
      </article>
    );
  } else {
    return null;
  }
};
